import { API_BASE_URL_AWARI } from '@/constants/api'
import { MentorsModel } from '@/models'
import setAuthorization from '@/utils/helpers/setAuthorization'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const mentorsRepository = createApi({
	reducerPath: 'mentorsRepository',
	baseQuery: fetchBaseQuery({
		prepareHeaders: setAuthorization,
		baseUrl: API_BASE_URL_AWARI,
	}),
	tagTypes: ['Mentors', 'Mentor', 'Individual', 'Match', 'Payments'],

	endpoints: (builder) => ({
		list: builder.query<
			MentorsModel.List,
			{
				page: string
				search?: string
				vertical?: string
				availabilities?: string
				tag?: string
			}
		>({
			query: ({
				page,
				search = '',
				vertical = '',
				availabilities = '',
				tag = '',
			}) => {
				const baseUrl = '/mentors?'
				const queryParams = [
					`page=${page}`,
					`search=${search}`,
					`vertical=${vertical}`,
					`tag=${tag.toLowerCase()}`,
					'mentorshipType=',
					availabilities && `availabilities[]=${availabilities}`,
				]
					.filter(Boolean)
					.join('&')

				return {
					url: `${baseUrl}${queryParams}`,
					method: 'GET',
				}
			},
			providesTags: ['Mentors'],
		}),
		get: builder.query<MentorsModel.GetMentor, { mentorSlug: string }>({
			query: ({ mentorSlug }) => ({
				url: `/mentors/${mentorSlug}`,
				method: 'GET',
			}),
			providesTags: ['Individual'],
		}),
		getAvailabilities: builder.query<MentorsModel.Availabilities, void>({
			query: () => ({
				url: `/availabilities`,
				method: 'GET',
			}),
			providesTags: ['Individual'],
		}),
		getReschedule: builder.mutation<
			Array<{
				day: string
				hours: string[]
			}>,
			{ mentorSlug: string; mentorshipId: string }
		>({
			query: ({ mentorSlug, mentorshipId }) => ({
				url: `/mentors/${mentorSlug}/availabilities?mentorshipId=${mentorshipId}`,
				method: 'GET',
			}),
			invalidatesTags: ['Individual'],
		}),
		getMutation: builder.mutation<
			MentorsModel.GetMentor,
			{ mentorSlug: string }
		>({
			query: ({ mentorSlug }) => ({
				url: `/mentors/${mentorSlug}`,
				method: 'GET',
			}),
			invalidatesTags: ['Mentor'],
		}),
		match: builder.query<MentorsModel.ShowSmall[], void>({
			query: () => ({
				url: `/students/match-mentor`,
				method: 'GET',
			}),
			providesTags: ['Match'],
		}),
		listPayments: builder.query<MentorsModel.PaymentsMentor[], void>({
			query: () => ({
				url: `/payouts`,
				method: 'GET',
			}),
			providesTags: ['Payments'],
		}),
		updateBankAccount: builder.mutation<
			void,
			{ pixKey: string; pixKeyType: string }
		>({
			query: (body) => ({
				url: `/account`,
				method: 'POST',
				body: body,
			}),
		}),
		updateAvailabilities: builder.mutation<void, MentorsModel.Availabilities>({
			query: (body) => ({
				url: `/availabilities`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Individual'],
		}),
	}),
})

export default mentorsRepository
